/**
 * O servidor de filmes da casa.
 *
 * Porte de `lib/features/jellyfin/data/services/jellyfin_api_service.dart` e do
 * modelo ao lado. É só leitura: listar, procurar e montar os endereços de
 * playlist, cartaz e legenda que a sessão de assistir junto usa.
 *
 * A chave vem do ambiente, como no Flutter (`JELLYFIN_API_KEY` no
 * `assets/env.txt`). Cada app pega a sua do jeito da sua plataforma — o site em
 * `VITE_JELLYFIN_KEY`, o celular no `extra` do `app.json` — e entrega aqui pelo
 * `configurarJellyfin`, na mesma hora em que chama o `ligar()`. Sem chave não há
 * chamada nenhuma: a tela mostra o mesmo recado do celular.
 */

const ENDERECO_PADRAO = 'http://localhost:8096';

/** O que o `/Items` do Jellyfin devolve, do jeito que a tela precisa. */
export interface FilmeDoJellyfin {
  id: string;
  nome: string;
  ano: string;
  sinopse: string;
  /** Em minutos. Zero quando o servidor não sabe. */
  minutos: number;
  /** A etiqueta da imagem, que faz o cartaz não vir do cache velho. */
  etiqueta: string;
}

interface FichaCrua {
  Id?: unknown;
  Name?: unknown;
  ProductionYear?: unknown;
  Overview?: unknown;
  RunTimeTicks?: unknown;
  ImageTags?: { Primary?: unknown } | null;
}

const texto = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Um "tick" do .NET é 100 nanossegundos; 600 milhões deles dão um minuto. */
function minutosDe(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return 0;
  return Math.round(v / 600000000);
}

function daFicha(cru: FichaCrua): FilmeDoJellyfin {
  return {
    id: texto(cru.Id),
    nome: texto(cru.Name),
    ano: cru.ProductionYear == null ? '' : String(cru.ProductionYear),
    sinopse: texto(cru.Overview),
    minutos: minutosDe(cru.RunTimeTicks),
    etiqueta: texto(cru.ImageTags?.Primary),
  };
}

let endereco = ENDERECO_PADRAO;
let chave = '';

/**
 * O endereço e a chave desta casa, entregues pelo app no arranque.
 *
 * Sem endereço fica o de fábrica, que é onde o Jellyfin nasce. Sem chave o
 * módulo inteiro fica quieto, e é isso que a tela mostra.
 */
export function configurarJellyfin(config: { endereco?: string; chave?: string }): void {
  endereco = config.endereco?.replace(/\/+$/, '') || ENDERECO_PADRAO;
  chave = config.chave ?? '';
}

/** Para o recado de "não deu para falar com o Jellyfin em …". */
export const enderecoDoJellyfin = (): string => endereco;

export const temChaveDoJellyfin = (): boolean => chave.length > 0;

/** O tempo de espera do Flutter: 10s para descobrir o dono, 15s para o resto. */
async function buscar(caminho: string, prazo: number): Promise<unknown | null> {
  const corte = new AbortController();
  const relogio = setTimeout(() => corte.abort(), prazo);
  try {
    const r = await fetch(`${endereco}${caminho}`, { signal: corte.signal });
    if (!r.ok) return null;
    return (await r.json()) as unknown;
  } catch {
    /* servidor fora do ar, chave errada, rede caída: para a tela é tudo o
       mesmo "não deu para falar com o Jellyfin". */
    return null;
  } finally {
    clearTimeout(relogio);
  }
}

const autenticacao = () => `api_key=${encodeURIComponent(chave)}`;

/**
 * O primeiro dono do servidor.
 *
 * Os endereços de item do Jellyfin são por pessoa, então nada começa sem isto.
 * Fica guardado: uma noite de filme inteira não precisa perguntar duas vezes.
 */
let donoGuardado: string | null = null;

async function dono(): Promise<string | null> {
  if (donoGuardado) return donoGuardado;
  const lista = await buscar(`/Users?${autenticacao()}`, 10_000);
  if (!Array.isArray(lista) || lista.length === 0) return null;
  const primeiro = lista[0] as { Id?: unknown };
  donoGuardado = texto(primeiro?.Id) || null;
  return donoGuardado;
}

function itensDe(corpo: unknown): FilmeDoJellyfin[] {
  const lista = (corpo as { Items?: unknown } | null)?.Items;
  if (!Array.isArray(lista)) return [];
  return lista.map((c) => daFicha(c as FichaCrua)).filter((f) => f.id !== '' && f.nome !== '');
}

const CAMPOS = 'Overview,RunTimeTicks,PrimaryImageAspectRatio';

/**
 * Os filmes que o Jellyfin indexou.
 *
 * `null` quer dizer "não deu para falar com o servidor" e é diferente de uma
 * lista vazia, que é "o servidor está no ar e ainda não achou nada" — a tela
 * mostra recados diferentes para cada um.
 */
export async function filmesDoJellyfin(): Promise<FilmeDoJellyfin[] | null> {
  const eu = await dono();
  if (!eu) return null;
  const corpo = await buscar(
    `/Users/${eu}/Items?${autenticacao()}` +
      `&includeItemTypes=Movie&recursive=true&fields=${CAMPOS}&limit=200`,
    15_000,
  );
  return corpo === null ? null : itensDe(corpo);
}

/** A mesma busca do servidor, por título ou nome de arquivo. */
export async function procurarNoJellyfin(termo: string): Promise<FilmeDoJellyfin[] | null> {
  const eu = await dono();
  if (!eu) return null;
  const corpo = await buscar(
    `/Users/${eu}/Items?${autenticacao()}` +
      `&searchTerm=${encodeURIComponent(termo)}` +
      `&includeItemTypes=Movie&recursive=true&fields=${CAMPOS}&limit=50`,
    15_000,
  );
  return corpo === null ? null : itensDe(corpo);
}

/** A playlist HLS do filme — é o que o tocador da sessão recebe. */
export function fluxoDoJellyfin(id: string): string {
  return (
    `${endereco}/Videos/${id}/master.m3u8?${autenticacao()}` +
    `&MediaSourceId=${id}&AudioCodec=copy`
  );
}

/** O cartaz do filme. */
export function cartazDoJellyfin(id: string, etiqueta = ''): string {
  const base =
    `${endereco}/Items/${id}/Images/Primary?${autenticacao()}` +
    `&maxWidth=300&maxHeight=450&quality=90`;
  return etiqueta ? `${base}&tag=${encodeURIComponent(etiqueta)}` : base;
}

/**
 * A primeira faixa de legenda de texto do filme.
 *
 * O Jellyfin indexa tanto a legenda embutida quanto o `.srt` ao lado do
 * arquivo; a marcada como padrão ganha, e na falta dela vale a primeira.
 */
export async function legendaDoJellyfin(id: string): Promise<string | null> {
  const eu = await dono();
  if (!eu) return null;
  const corpo = await buscar(
    `/Items/${id}/PlaybackInfo?${autenticacao()}&userId=${eu}&MediaSourceId=${id}`,
    15_000,
  );
  const fontes = (corpo as { MediaSources?: unknown } | null)?.MediaSources;
  if (!Array.isArray(fontes) || fontes.length === 0) return null;
  const faixas = (fontes[0] as { MediaStreams?: unknown })?.MediaStreams;
  if (!Array.isArray(faixas)) return null;

  let reserva: number | null = null;
  for (const cru of faixas) {
    const f = cru as {
      Type?: unknown;
      IsTextSubtitleStream?: unknown;
      Index?: unknown;
      IsDefault?: unknown;
    };
    if (f.Type !== 'Subtitle' || f.IsTextSubtitleStream !== true) continue;
    if (typeof f.Index !== 'number') continue;
    if (reserva === null) reserva = f.Index;
    if (f.IsDefault === true) return enderecoDaLegenda(id, f.Index);
  }
  return reserva === null ? null : enderecoDaLegenda(id, reserva);
}

/** A legenda em WebVTT, que o `<video>` desenha sozinho. */
function enderecoDaLegenda(id: string, indice: number): string {
  return `${endereco}/Videos/${id}/${id}/Subtitles/${indice}/0/stream.vtt?${autenticacao()}`;
}
